/**
 * Event creation wizard — two-step form:
 *   Step 1: Event details (name, date, location)
 *   Step 2: Course configuration (preset or custom per-station target counts)
 *
 * Requirements: 1.1, 1.2, 1.4, 1.5, 1.6
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout, Button, DonationButton, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiError } from '@scoring/frontend-shared';
import { useApiClient } from '../hooks/useApiClient.js';

type CourseMode = 'preset' | 'custom';
type PresetType = '50/10' | '100/10';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EventDetailsForm {
  name: string;
  date: string;
  location: string;
}

interface CourseConfigForm {
  mode: CourseMode;
  presetType: PresetType;
  /** Per-station target counts for custom mode. */
  stationTargets: number[];
}

interface FieldErrors {
  name?: string;
  date?: string;
  location?: string;
  stationTargets?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_STATION_COUNT = 10;
const PRESET_OPTIONS: { value: PresetType; label: string }[] = [
  { value: '50/10', label: '50 targets / 10 stations' },
  { value: '100/10', label: '100 targets / 10 stations' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EventCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const apiClient = useApiClient();

  // Wizard step (0-indexed)
  const [step, setStep] = useState(0);

  // Step 1 state
  const [details, setDetails] = useState<EventDetailsForm>({
    name: '',
    date: '',
    location: '',
  });

  // Step 2 state
  const [courseConfig, setCourseConfig] = useState<CourseConfigForm>({
    mode: 'preset',
    presetType: '50/10',
    stationTargets: Array.from<number>({ length: DEFAULT_STATION_COUNT }).fill(5),
  });

  // Shared state
  const [errors, setErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  function validateStep1(): boolean {
    const fieldErrors: FieldErrors = {};
    if (!details.name.trim()) {
      fieldErrors.name = 'Event name is required.';
    }
    if (!details.date) {
      fieldErrors.date = 'Event date is required.';
    }
    if (!details.location.trim()) {
      fieldErrors.location = 'Location is required.';
    }
    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  }

  function validateStep2(): boolean {
    const fieldErrors: FieldErrors = {};
    if (courseConfig.mode === 'custom') {
      const hasInvalid = courseConfig.stationTargets.some(
        (t) => !Number.isInteger(t) || t < 0,
      );
      if (hasInvalid) {
        fieldErrors.stationTargets =
          'Each station must have a non-negative integer target count.';
      }
    }
    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  function handleNext(): void {
    if (validateStep1()) {
      setStep(1);
    }
  }

  function handleBack(): void {
    setErrors({});
    setApiError(null);
    setStep(0);
  }

  // ---------------------------------------------------------------------------
  // Submission
  // ---------------------------------------------------------------------------

  async function handleSubmit(): Promise<void> {
    if (!validateStep2()) return;

    setSubmitting(true);
    setApiError(null);

    try {
      const body: Record<string, unknown> = {
        name: details.name.trim(),
        date: details.date,
        location: details.location.trim(),
        courseMode: courseConfig.mode,
      };

      if (courseConfig.mode === 'preset') {
        body.presetType = courseConfig.presetType;
      } else {
        body.stationTargets = courseConfig.stationTargets;
      }

      const result = await apiClient.request<{ event: { id: string } }>(
        'POST',
        '/events',
        { body },
      );

      navigate(`/events/${result.event.id}`);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setApiError(apiErr.message ?? 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Course config helpers
  // ---------------------------------------------------------------------------

  function handleModeChange(mode: CourseMode): void {
    setCourseConfig((prev) => ({ ...prev, mode }));
    setErrors({});
  }

  function handlePresetChange(presetType: PresetType): void {
    setCourseConfig((prev) => ({ ...prev, presetType }));
  }

  function handleStationCountChange(count: number): void {
    const clamped = Math.max(1, Math.min(count, 50));
    setCourseConfig((prev) => {
      const existing = prev.stationTargets;
      const newTargets = Array.from<number>({ length: clamped }).map(
        (_, i) => existing[i] ?? 5,
      );
      return { ...prev, stationTargets: newTargets };
    });
  }

  function handleStationTargetChange(index: number, value: string): void {
    const parsed = value === '' ? 0 : parseInt(value, 10);
    const target = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    setCourseConfig((prev) => {
      const updated = [...prev.stationTargets];
      updated[index] = target;
      return { ...prev, stationTargets: updated };
    });
  }

  // ---------------------------------------------------------------------------
  // Computed values
  // ---------------------------------------------------------------------------

  const totalTargets =
    courseConfig.mode === 'preset'
      ? courseConfig.presetType === '50/10'
        ? 50
        : 100
      : courseConfig.stationTargets.reduce((sum, t) => sum + t, 0);

  const stationCount =
    courseConfig.mode === 'preset' ? 10 : courseConfig.stationTargets.length;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <AppLayout title="Create New Event" footer={<DonationButton />}>
      <div style={{ maxWidth: '36rem', margin: '0 auto' }}>
        {/* Step indicator */}
        <nav aria-label="Wizard steps" style={{ marginBottom: '1.5rem' }}>
          <ol style={{ display: 'flex', gap: '1rem', listStyle: 'none', padding: 0, margin: 0 }}>
            <li style={{ fontWeight: step === 0 ? 700 : 400, color: step === 0 ? 'var(--color-brand-green, #1B4332)' : undefined }}>
              1. Event Details
            </li>
            <li style={{ fontWeight: step === 1 ? 700 : 400, color: step === 1 ? 'var(--color-brand-green, #1B4332)' : undefined }}>
              2. Course Configuration
            </li>
          </ol>
        </nav>

        {apiError && (
          <ErrorBanner title="Error" onDismiss={() => setApiError(null)}>
            {apiError}
          </ErrorBanner>
        )}

        {/* Step 1: Event Details */}
        {step === 0 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
            noValidate
          >
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
                Event Details
              </legend>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="event-name" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                  Event Name
                </label>
                <input
                  id="event-name"
                  type="text"
                  value={details.name}
                  onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'event-name-error' : undefined}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius-button, 8px)', border: '1px solid var(--color-border, #E5E7EB)' }}
                />
                {errors.name && (
                  <p id="event-name-error" role="alert" style={{ color: 'var(--color-brand-red, #DC2F02)', fontSize: 'var(--font-size-body-sm, 0.875rem)', margin: '0.25rem 0 0' }}>
                    {errors.name}
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="event-date" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                  Date
                </label>
                <input
                  id="event-date"
                  type="date"
                  value={details.date}
                  onChange={(e) => setDetails((d) => ({ ...d, date: e.target.value }))}
                  aria-invalid={!!errors.date}
                  aria-describedby={errors.date ? 'event-date-error' : undefined}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius-button, 8px)', border: '1px solid var(--color-border, #E5E7EB)' }}
                />
                {errors.date && (
                  <p id="event-date-error" role="alert" style={{ color: 'var(--color-brand-red, #DC2F02)', fontSize: 'var(--font-size-body-sm, 0.875rem)', margin: '0.25rem 0 0' }}>
                    {errors.date}
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="event-location" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                  Location
                </label>
                <input
                  id="event-location"
                  type="text"
                  value={details.location}
                  onChange={(e) => setDetails((d) => ({ ...d, location: e.target.value }))}
                  aria-invalid={!!errors.location}
                  aria-describedby={errors.location ? 'event-location-error' : undefined}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius-button, 8px)', border: '1px solid var(--color-border, #E5E7EB)' }}
                />
                {errors.location && (
                  <p id="event-location-error" role="alert" style={{ color: 'var(--color-brand-red, #DC2F02)', fontSize: 'var(--font-size-body-sm, 0.875rem)', margin: '0.25rem 0 0' }}>
                    {errors.location}
                  </p>
                )}
              </div>
            </fieldset>

            <Button type="submit">Next: Course Configuration</Button>
          </form>
        )}

        {/* Step 2: Course Configuration */}
        {step === 1 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
            noValidate
          >
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
                Course Configuration
              </legend>

              {/* Mode selection */}
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Configuration Mode
                </span>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input
                      type="radio"
                      name="courseMode"
                      value="preset"
                      checked={courseConfig.mode === 'preset'}
                      onChange={() => handleModeChange('preset')}
                    />
                    Preset
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input
                      type="radio"
                      name="courseMode"
                      value="custom"
                      checked={courseConfig.mode === 'custom'}
                      onChange={() => handleModeChange('custom')}
                    />
                    Custom
                  </label>
                </div>
              </div>

              {/* Preset options */}
              {courseConfig.mode === 'preset' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="preset-type" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                    Preset
                  </label>
                  <select
                    id="preset-type"
                    value={courseConfig.presetType}
                    onChange={(e) => handlePresetChange(e.target.value as PresetType)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius-button, 8px)', border: '1px solid var(--color-border, #E5E7EB)' }}
                  >
                    {PRESET_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Custom mode */}
              {courseConfig.mode === 'custom' && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label htmlFor="station-count" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                      Number of Stations
                    </label>
                    <input
                      id="station-count"
                      type="number"
                      min={1}
                      max={50}
                      value={stationCount}
                      onChange={(e) => handleStationCountChange(parseInt(e.target.value, 10) || 1)}
                      style={{ width: '6rem', padding: '0.5rem', borderRadius: 'var(--radius-button, 8px)', border: '1px solid var(--color-border, #E5E7EB)' }}
                    />
                  </div>

                  <div
                    role="group"
                    aria-label="Per-station target counts"
                    style={{ maxHeight: '16rem', overflowY: 'auto', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: 'var(--radius-button, 8px)', padding: '0.5rem' }}
                  >
                    {courseConfig.stationTargets.map((target, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                        <label
                          htmlFor={`station-${idx}`}
                          style={{ minWidth: '5.5rem', fontSize: 'var(--font-size-body-sm, 0.875rem)' }}
                        >
                          Station {idx + 1}
                        </label>
                        <input
                          id={`station-${idx}`}
                          type="number"
                          min={0}
                          value={target}
                          onChange={(e) => handleStationTargetChange(idx, e.target.value)}
                          style={{ width: '5rem', padding: '0.35rem', borderRadius: 'var(--radius-button, 8px)', border: '1px solid var(--color-border, #E5E7EB)' }}
                        />
                      </div>
                    ))}
                  </div>

                  {errors.stationTargets && (
                    <p role="alert" style={{ color: 'var(--color-brand-red, #DC2F02)', fontSize: 'var(--font-size-body-sm, 0.875rem)', margin: '0.25rem 0 0' }}>
                      {errors.stationTargets}
                    </p>
                  )}
                </div>
              )}

              {/* Running total */}
              <div
                aria-live="polite"
                style={{
                  padding: '0.75rem 1rem',
                  background: 'var(--color-bg-page, #FAFBFC)',
                  borderRadius: 'var(--radius-button, 8px)',
                  marginBottom: '1.5rem',
                  fontWeight: 500,
                }}
              >
                Total: {totalTargets} targets across {stationCount} station{stationCount !== 1 ? 's' : ''}
              </div>
            </fieldset>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button variant="secondary" onClick={handleBack} disabled={submitting}>
                Back
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Spinner size="1rem" label="Creating event…" /> : 'Create Event'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </AppLayout>
  );
}
